import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatDateTime, isAdminRole } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Bell, Mail, MessageCircle, CheckCircle2, XCircle, PlayCircle,
  Settings, AlertTriangle, ExternalLink, Loader2, Send,
} from "lucide-react";
import { NavLink } from "react-router-dom";

const CODE_STYLE = "font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded text-slate-700";

export default function Notifications() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null); // { ok, sender, to, result?, error? }
  const canAdmin = isAdminRole(user?.role) || user?.role === "manager";

  useEffect(() => {
    if (user?.email && !testEmailTo) setTestEmailTo(user.email);
  }, [user?.email, testEmailTo]);

  const sendTestEmail = async () => {
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const { data } = await api.post("/notifications/test-email", { to: testEmailTo || undefined });
      setTestEmailResult(data);
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.error(`Test failed: ${data.error?.slice(0, 140) || "unknown error"}`);
    } catch (e) {
      const err = formatApiError(e.response?.data?.detail) || "Request failed";
      setTestEmailResult({ ok: false, error: err, to: testEmailTo });
      toast.error(err);
    } finally {
      setTestEmailSending(false);
    }
  };

  const load = async () => {
    try {
      const reqs = [api.get("/notifications")];
      if (canAdmin) reqs.push(api.get("/notifications/config"));
      const [h, c] = await Promise.all(reqs);
      setHistory(h.data);
      if (c) setConfig(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runCheck = async (dry = false) => {
    setChecking(true);
    try {
      const { data } = await api.post(`/notifications/check?dry_run=${dry}`);
      toast.success(
        dry
          ? `Dry run: ${data.alerts_triggered} alerts would fire`
          : `Sent ${data.emails_sent} email(s), ${data.sms_sent} SMS · ${data.skipped_duplicates} skipped`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setChecking(false); }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Alerts</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Notifications & Renewals</h1>
          <p className="text-slate-500 mt-1 text-sm">Warranty and AMC expiry alerts — Email + SMS · Auto-checked every 6 hours</p>
        </div>
        {canAdmin && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => runCheck(true)}
              disabled={checking}
              data-testid="dry-run-btn"
            >
              {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              Dry Run
            </Button>
            <Button
              onClick={() => runCheck(false)}
              disabled={checking}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="send-alerts-btn"
            >
              {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Run Alert Check Now
            </Button>
          </div>
        )}
      </div>

      {/* Config status */}
      {config && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChannelCard
            title="Email (Google Workspace SMTP)"
            icon={Mail}
            configured={config.email?.configured}
            detail={config.email?.configured
              ? `From: ${config.email.sender} · Relay: ${config.email.host}:${config.email.port}`
              : `Relay: ${config.email?.host || "(SMTP_HOST unset)"}`}
            setupUrl="https://admin.google.com/ac/apps/gmail/smtprelay"
            setupText="Google Admin → Apps → Gmail → Routing → SMTP relay service"
          />
          <ChannelCard
            title="WhatsApp (Automate.chats)"
            icon={MessageCircle}
            configured={config.whatsapp?.configured}
            detail={config.whatsapp?.configured
              ? `Provider: ${config.whatsapp.provider || "Automate.chats"}`
              : "AUTOMATECHATS_API_KEY not set in backend .env"}
            setupUrl="/email-templates"
            setupText="Configure per-trigger templates at Email Configuration"
          />
        </div>
      )}

      {/* Send Test Email — surfaces exact SMTP errors when 'Active' but delivery fails */}
      {canAdmin && config?.email?.configured && (
        <Card className="border-slate-200" data-testid="test-email-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Send className="w-4 h-4 text-[#7C1D3A]" /> Send Test Email
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-3">
            <p className="text-xs text-slate-500">
              Fires a real email through Google Workspace SMTP Relay using the configured sender (<code className="font-mono bg-slate-100 px-1 rounded">{config.email.sender}</code>). Use this to diagnose why &ldquo;Active&rdquo; is not translating to actual delivery — the exact SMTP response is shown below.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
                className="max-w-xs"
                data-testid="test-email-to"
              />
              <Button
                onClick={sendTestEmail}
                disabled={testEmailSending || !testEmailTo}
                className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
                data-testid="test-email-send-btn"
              >
                {testEmailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            </div>
            {testEmailResult && (
              testEmailResult.ok ? (
                <Alert className="border-emerald-200 bg-emerald-50" data-testid="test-email-success">
                  <CheckCircle2 className="w-4 h-4 !text-emerald-600" />
                  <AlertDescription className="text-emerald-900 text-sm">
                    Email accepted by the SMTP relay. Message-ID: <code className="font-mono">{testEmailResult.result}</code>. Delivered to <code className="font-mono">{testEmailResult.to}</code>. If it does not arrive within a minute, check the recipient&apos;s spam folder or the sending mailbox&apos;s Sent items.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-rose-200 bg-rose-50" data-testid="test-email-failure">
                  <XCircle className="w-4 h-4 !text-rose-600" />
                  <AlertDescription className="text-rose-900 text-sm space-y-1">
                    <div className="font-semibold">Delivery failed</div>
                    <div className="font-mono text-xs bg-white/70 border border-rose-200 rounded p-2 whitespace-pre-wrap break-words">
                      {testEmailResult.error}
                    </div>
                    {/auth|535|password|username|app password/i.test(String(testEmailResult.error)) && (
                      <div className="text-xs pt-1">
                        <b>Common cause:</b> Google requires an <b>App Password</b> (16 characters, generated at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/apppasswords</a>) — not the regular Google account password. 2-step verification must be enabled on the mailbox first.
                      </div>
                    )}
                    {/sender refused|relay access denied|550|553/i.test(String(testEmailResult.error)) && (
                      <div className="text-xs pt-1">
                        <b>Common cause:</b> The sending domain is not authorised in the SMTP Relay settings. Go to <a href="https://admin.google.com/ac/apps/gmail/smtprelay" target="_blank" rel="noreferrer" className="underline">Google Admin → Apps → Gmail → Routing → SMTP relay service</a> and either (a) tick &ldquo;Only accept mail from the specified email addresses&rdquo; and add <code className="font-mono">{config.email.sender}</code>, or (b) allow the entire domain.
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )
            )}
          </CardContent>
        </Card>
      )}

      {/* Setup instructions if not configured */}
      {canAdmin && config && (!config.email?.configured || !config.whatsapp?.configured) && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="w-4 h-4 !text-amber-600" />
          <AlertDescription className="text-amber-900 text-sm space-y-3">
            <div className="font-semibold">Setup required to actually send alerts</div>
            {!config.email?.configured && (
              <div className="space-y-1.5">
                <div className="font-medium">📧 Email via Google Workspace SMTP Relay (10,000 recipients/day, uses your own @bbsglobal.in sender)</div>
                <ol className="list-decimal ml-5 space-y-0.5 text-xs">
                  <li>In <a href="https://admin.google.com/ac/apps/gmail/smtprelay" target="_blank" rel="noreferrer" className="underline text-[#7C1D3A]">Google Admin → Apps → Google Workspace → Gmail → Routing → SMTP relay service <ExternalLink className="w-3 h-3 inline" /></a>, click <b>Add another rule</b>. Name it &ldquo;Asset IQ&rdquo;, tick <b>Only accept mail from the specified email addresses</b> and add <code className={CODE_STYLE}>{config.email?.sender || "assetiq@bbsglobal.in"}</code>. Enable <b>Require SMTP authentication</b> + <b>Require TLS encryption</b>. Save.</li>
                  <li>On the authenticating mailbox (e.g. <code className={CODE_STYLE}>assetiq@bbsglobal.in</code> or a licensed user that owns this alias), enable <b>2-Step Verification</b> at <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/security</a>.</li>
                  <li>Generate an App Password at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-[#7C1D3A]">myaccount.google.com/apppasswords <ExternalLink className="w-3 h-3 inline" /></a> — choose &ldquo;Mail&rdquo; + custom name &ldquo;Asset IQ&rdquo;. Copy the 16-character password (no spaces).</li>
                  <li>Paste the credentials into <code className={CODE_STYLE}>/app/backend/.env</code>: <code className={CODE_STYLE}>SMTP_USERNAME</code> = the licensed mailbox address, <code className={CODE_STYLE}>SMTP_PASSWORD</code> = the App Password, <code className={CODE_STYLE}>SMTP_FROM</code> = the alias to display (e.g. <code className={CODE_STYLE}>assetiq@bbsglobal.in</code>).</li>
                  <li>Share the App Password with me and I&apos;ll restart the backend for you.</li>
                </ol>
              </div>
            )}
            {!config.whatsapp?.configured && (
              <div className="space-y-1.5">
                <div className="font-medium">💬 WhatsApp via Automate.chats</div>
                <ol className="list-decimal ml-5 space-y-0.5 text-xs">
                  <li>Sign up / log in to <a href="https://automate.chats/" target="_blank" rel="noreferrer" className="underline text-[#7C1D3A]">automate.chats <ExternalLink className="w-3 h-3 inline" /></a> and create a WhatsApp template for the alerts you want to fan out.</li>
                  <li>Copy the API key from Automate.chats → Settings → API Keys.</li>
                  <li>Paste it into <code className={CODE_STYLE}>/app/backend/.env</code> as <code className={CODE_STYLE}>AUTOMATECHATS_API_KEY</code>, then restart the backend.</li>
                  <li>Head to <NavLink to="/email-templates" className="underline text-[#7C1D3A]">Email Configuration</NavLink> and paste the template ID + variable mapping into the WhatsApp panel for each trigger.</li>
                </ol>
              </div>
            )}
            <div className="text-xs opacity-80 pt-1">
              Even without keys, the system runs on schedule and <b>logs every alert</b> below. You can review what would have gone out and enable delivery anytime.
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Schedule */}
      {config && (
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#7C1D3A]" /> Alert Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Warranty Expiry</div>
              <div className="flex gap-2 flex-wrap">
                {config.schedule.warranty_days_before.map((d) => (
                  <Badge key={d} className="bg-amber-100 text-amber-800 border-amber-200 font-mono">{d}d before</Badge>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">AMC Renewal</div>
              <div className="flex gap-2 flex-wrap">
                {config.schedule.amc_days_before.map((d) => (
                  <Badge key={d} className="bg-blue-100 text-blue-800 border-blue-200 font-mono">{d}d before</Badge>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Recipients (Roles)</div>
              <div className="flex gap-2 flex-wrap">
                {config.schedule.default_roles.map((r) => (
                  <Badge key={r} variant="outline" className="text-[#7C1D3A] border-[#f5c4d3] capitalize text-xs">
                    {r.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#7C1D3A]" /> Alert History ({history.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : history.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No alerts have been triggered yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Asset</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Days Left</th>
                    <th className="px-4 py-3">Recipient</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">SMS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="notification-history">
                  {history.map((n) => (
                    <tr key={n.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {formatDateTime(n.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <NavLink to={`/assets/${n.asset_id}`} className="text-slate-900 hover:text-[#7C1D3A]">
                          <span className="font-mono text-xs text-slate-500">{n.asset_code}</span>{" "}
                          <span className="font-medium">{n.asset_name}</span>
                        </NavLink>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={n.alert_type === "warranty" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-blue-100 text-blue-800 border-blue-200"}>
                          {n.alert_type === "warranty" ? "Warranty" : "AMC"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-slate-700">
                        <span className={n.days_before <= 7 ? "text-red-600 font-bold" : ""}>{n.days_before}d</span>
                        <span className="text-slate-400 text-xs ml-2">({formatDate(n.expiry_date)})</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        <div>{n.recipient_name}</div>
                        <div className="text-slate-400 font-mono">{n.recipient_email || "—"}</div>
                      </td>
                      <td className="px-4 py-3"><StatusPill status={n.email_status} /></td>
                      <td className="px-4 py-3"><StatusPill status={n.sms_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ChannelCard({ title, icon: Icon, configured, detail, setupUrl }) {
  return (
    <Card className={configured ? "border-green-200 bg-green-50/30" : "border-amber-200 bg-amber-50/30"}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${configured ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-heading font-semibold text-slate-900">{title}</span>
            {configured ? (
              <Badge className="bg-green-100 text-green-800 border-green-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Not configured</Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">{detail}</div>
          {!configured && (
            <a href={setupUrl} target="_blank" rel="noreferrer" className="mt-2 text-xs text-[#7C1D3A] hover:underline inline-flex items-center gap-1">
              Setup guide <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: ["bg-green-100 text-green-700 border-green-200", <CheckCircle2 className="w-3 h-3" />],
    failed: ["bg-red-100 text-red-700 border-red-200", <XCircle className="w-3 h-3" />],
    dry_run: ["bg-slate-100 text-slate-600 border-slate-200", <PlayCircle className="w-3 h-3" />],
    no_email: ["bg-slate-50 text-slate-400 border-slate-200", null],
    no_phone: ["bg-slate-50 text-slate-400 border-slate-200", null],
  };
  const [cls, icon] = map[status] || ["bg-slate-50 text-slate-400", null];
  return (
    <Badge className={`${cls} border text-xs capitalize`}>
      {icon && <span className="mr-1">{icon}</span>}
      {(status || "—").replace(/_/g, " ")}
    </Badge>
  );
}
